import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { listsApi } from '../services/api';

const ListsContext = createContext();

export const useLists = () => {
  const context = useContext(ListsContext);
  if (!context) {
    throw new Error('useLists must be used within a ListsProvider');
  }
  return context;
};

export const ListsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [lists, setLists] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadLists = useCallback(async () => {
    if (!isAuthenticated || !user?.username) return;
    try {
      const data = await listsApi.getLists();
      setLists(data.lists || []);
      setLoaded(true);
    } catch (err) {
      console.error('[ListsContext] Failed to load lists:', err.message);
    }
  }, [isAuthenticated, user?.username]);

  useEffect(() => {
    if (isAuthenticated && user?.username) {
      loadLists();
    } else {
      setLists([]);
      setLoaded(false);
    }
  }, [isAuthenticated, user?.username, loadLists]);

  const favouritesList = lists.find(l => l.is_favourite) || null;

  const isInList = useCallback((listId, username) => {
    const list = lists.find(l => l.id === listId);
    return list ? list.members.includes(username.toLowerCase()) : false;
  }, [lists]);

  const isInFavourites = useCallback((username) => {
    return favouritesList ? favouritesList.members.includes(username.toLowerCase()) : false;
  }, [favouritesList]);

  const createList = useCallback(async (name) => {
    const data = await listsApi.createList(name);
    setLists(prev => [...prev, data.list]);
    return data.list;
  }, []);

  const deleteList = useCallback(async (listId) => {
    await listsApi.deleteList(listId);
    setLists(prev => prev.filter(l => l.id !== listId));
  }, []);

  const renameList = useCallback(async (listId, newName) => {
    const data = await listsApi.renameList(listId, newName);
    setLists(prev => prev.map(l =>
      l.id === listId ? { ...l, name: data.list.name } : l
    ));
    return data.list;
  }, []);

  const addToList = useCallback(async (listId, username) => {
    await listsApi.addMember(listId, username);
    setLists(prev => prev.map(l =>
      l.id === listId && !l.members.includes(username.toLowerCase())
        ? { ...l, members: [...l.members, username.toLowerCase()] }
        : l
    ));
  }, []);

  const removeFromList = useCallback(async (listId, username) => {
    await listsApi.removeMember(listId, username);
    setLists(prev => prev.map(l =>
      l.id === listId
        ? { ...l, members: l.members.filter(u => u !== username.toLowerCase()) }
        : l
    ));
  }, []);

  const toggleListMember = useCallback(async (listId, username) => {
    if (isInList(listId, username)) {
      await removeFromList(listId, username);
    } else {
      await addToList(listId, username);
    }
  }, [isInList, addToList, removeFromList]);

  const toggleFavourites = useCallback(async (username) => {
    if (!favouritesList) return;
    await toggleListMember(favouritesList.id, username);
  }, [favouritesList, toggleListMember]);

  return (
    <ListsContext.Provider value={{
      lists,
      loaded,
      favouritesList,
      isInList,
      isInFavourites,
      createList,
      renameList,
      deleteList,
      addToList,
      removeFromList,
      toggleListMember,
      toggleFavourites,
      reload: loadLists,
    }}>
      {children}
    </ListsContext.Provider>
  );
};
